/**
 * AgentOsDemoShell.jsx
 *
 * Shared layout used by the 3 Phase 1 sandbox demos (Product Desc,
 * Cart Recovery, Newsletter). Same Profitron-style dark aesthetic as
 * `AgentOsDemoReviewReply`. Tailwind classes are kept as literals so
 * JIT picks them up — we don't interpolate the accent here.
 *
 * Caller responsibilities:
 *  - render <Form> inside `left` (controlled inputs + submit)
 *  - render <Output> inside `right` (loading/error/result)
 */
import { Bot, ArrowRight } from "lucide-react";
import { AgentOsPixelTracker, LiveLeadCounter } from "../components/agent-os/AgentOsShared";

export default function AgentOsDemoShell({ testid, h1, lead, left, right }) {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid={testid}>
      <AgentOsPixelTracker event="ViewContent" />

      <div className="border-b border-emerald-900/30 bg-black/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center">
              <Bot className="w-5 h-5 text-black" />
            </div>
            <span className="font-black tracking-tight">
              TopRankPulse <span className="text-emerald-400">· Agent OS · Demo</span>
            </span>
          </a>
          <a
            href="/agent-os/eshop#lead-form"
            data-testid={`${testid}-topbar-cta`}
            className="px-4 py-2 bg-emerald-400 hover:bg-emerald-300 text-black text-xs font-black uppercase tracking-widest"
          >
            Spusti pre svoj e-shop →
          </a>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-6 py-12">
        <div className="text-center mb-10">
          <div className="inline-flex mb-4">
            <LiveLeadCounter variant="eshop" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-black mb-3" data-testid={`${testid}-h1`}>
            {h1}
          </h1>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">{lead}</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {left}
          {right}
        </div>

        <div className="mt-16 p-8 bg-gradient-to-r from-emerald-950/40 to-black border border-emerald-700/40 text-center">
          <h2 className="text-2xl sm:text-3xl font-black mb-3">
            Páči sa ti to? Aktivuj <span className="text-emerald-400">naživo</span> pre svoj e-shop.
          </h2>
          <p className="text-sm text-gray-400 mb-6 max-w-xl mx-auto">
            14 dní zadarmo bez karty. 26 jazykov. €79–99 / mes.
          </p>
          <a
            href="/agent-os/onboard"
            data-testid={`${testid}-bottom-cta`}
            className="inline-flex items-center gap-2 px-7 py-3 bg-emerald-400 hover:bg-emerald-300 text-black font-black uppercase tracking-widest text-sm"
          >
            Spusti onboarding (60s) <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </main>

      <footer className="border-t border-gray-900 py-8 text-center text-xs text-gray-600">
        © 2026 TopRankPulse · Agent OS
      </footer>
    </div>
  );
}
